import type { FC } from "react";
import { useCallback, useMemo } from "react";
import { Pressable } from "react-native";
import { router, useGlobalSearchParams } from "expo-router";
import { Chips, Delete, Warning } from "@ptsd/uikit";
import { useTheme, XStack } from "tamagui";

import type { TSpheres } from "@edenlabllc/ptsd-validators";

import type { RouterOutputs } from "~/utils/api";
import { api } from "~/utils/api";

type Sphere = RouterOutputs["scores"]["spheres"]["data"][number];

const HMT_DOMAIN: TSpheres = "hmt";

export const ResultNavbar: FC = () => {
  const { data: sphere } = api.scores.spheres.useQuery({});
  const slug = (useGlobalSearchParams().slug ?? HMT_DOMAIN) as TSpheres;

  const items = useMemo(() => {
    if (!sphere?.data) return [];
    return sphere.data.filter((item) => Boolean(item.domain));
  }, [sphere?.data]);

  return (
    <Chips.Container
      variant="borderless"
      backgroundColor="$white"
      paddingHorizontal={0}
      paddingVertical={0}
      borderRadius={0}
      paddingTop={"$3"}
      paddingBottom={"$3"}
    >
      <XStack gap={"$1"} alignItems="center" flexWrap="wrap">
        {items.map((item) => (
          <SphereChip
            key={item.domain}
            item={item}
            slug={slug}
            isPaywall={sphere?.isPaywall}
          />
        ))}
      </XStack>
    </Chips.Container>
  );
};

const SphereChip: FC<{ item: Sphere; slug: TSpheres; isPaywall?: boolean }> = ({
  item,
  slug,
  isPaywall = false,
}) => {
  const theme = useTheme();

  const disabled = isPaywall && item.domain !== HMT_DOMAIN;
  const selected = slug === item.domain;
  const color = selected ? theme.white.val : theme["darkgreen-500"].val;
  const bgColor = theme["lightgreen-500"].val;

  const handlePress = useCallback(
    () => router.replace(`/results/${item.domain}`),
    [item.domain],
  );

  return (
    <Pressable key={item.domain} disabled={disabled} onPress={handlePress}>
      <Chips.Chip
        backgroundColor={bgColor}
        opacity={disabled ? 0.4 : 1}
        selected={slug === item.domain}
        paddingHorizontal={"$2"}
      >
        <XStack gap={"$0.5"} alignItems="center">
          <SphereLevelIcon item={item} />
          <Chips.Description color={color} fontSize="$3">
            {item.title}
          </Chips.Description>
          {item.domain !== HMT_DOMAIN && (
            <Chips.Description color={color} fontSize="$3" fontWeight="$6">
              ({item.score})
            </Chips.Description>
          )}
        </XStack>
      </Chips.Chip>
    </Pressable>
  );
};

const SphereLevelIcon: FC<{ item: Sphere }> = ({ item }) => {
  if (item.domain === HMT_DOMAIN) return null;
  switch (item.level) {
    case "CRITICAL":
      return <Delete />;
    case "MIDDLE":
      return <Warning />;
    default:
      return null;
  }
};
